import { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { Button } from '../atoms';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { TenantListTable, TenantFilters, initialTenantFilters } from '../organism';
import { TenantFilterInput, useGetTenantsQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const textsData = {
  en: {
    title: 'Tenants',
    buttonText: '+ Add Tenant'
  },
  es: {
    title: 'Inquilinos',
    buttonText: '+ Agregar Inquilino'
  }
}

const ButtonContainer = styled(Col)`
  display: flex;
  align-items: center;
`

export const TenantListPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('tenants')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as TenantFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('tenants')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<TenantFilterInput>(() => {
    const savedState = getListState('tenants')
    return (savedState?.filters as TenantFilterInput) ?? initialTenantFilters
  });

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('tenants', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: TenantFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const { data } = useGetTenantsQuery({
    variables: {
      filterInput: { ...filters, page: currentPage.toString() },
    },
    fetchPolicy: 'network-only',
  });

  const currentDataCount = data?.tenants?.items?.length || 0;
  const totalPages = data?.tenants?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <Container fluid>
      <Row>
        <Col>
            <h2>{texts.title}</h2>
        </Col>
        <ButtonContainer xs="content">
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.TENANT}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </ButtonContainer>
      </Row>
      <Row>
        <Col>
          <TenantFilters filters={filters} onFilterChange={handleFilterChange} />
        </Col>
      </Row>
      <Row>
        <Col>
          <TenantListTable filters={{ ...filters, page: currentPage.toString() }} />
        </Col>
      </Row>
      {showPagination && (
        <Row>
          <Col>
            <Pagination 
              currentPage={currentPage} 
              onPageChange={handlePageChange} 
              currentDataCount={currentDataCount}
              pageSize={filters.pageSize || null}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      )}
    </Container>
  )
}
